import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowUpRight } from 'lucide-react';
import { NAV_ITEMS, LOGOS } from '@/lib/constants';
import { motion, AnimatePresence } from 'framer-motion';

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const dismissed = sessionStorage.getItem('rebrand-banner-dismissed');
    if (dismissed) setShowBanner(false);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveMenu(null);
  }, [location]);

  const dismissBanner = () => {
    setShowBanner(false);
    sessionStorage.setItem('rebrand-banner-dismissed', 'true');
  };

  return (
    <>
      {/* Rebrand Banner */}
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-[#1A1008] text-white text-center text-[13px] py-2.5 px-4 relative z-[60] tracking-wide"
          >
            <span className="opacity-70">HR Anexi is now </span>
            <Link to="/our-new-chapter" className="font-semibold text-[#F59E0B] hover:text-[#E8642C] transition-colors">
              Anexi Consulting
            </Link>
            <span className="opacity-70">. </span>
            <Link to="/our-new-chapter" className="underline underline-offset-2 opacity-60 hover:opacity-100 transition-opacity">
              Here's why
            </Link>
            <button
              onClick={dismissBanner}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 hover:bg-white/10 rounded-full transition-colors"
              data-testid="rebrand-banner-dismiss"
              aria-label="Dismiss banner"
            >
              <X size={12} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/97 backdrop-blur-xl shadow-[0_1px_0_rgba(61,28,17,0.06)]'
            : 'bg-[#FAF5EF]'
        }`}
        data-testid="main-header"
      >
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-[72px]">
            {/* Logo */}
            <Link to="/" className="flex items-center relative z-50" data-testid="header-logo">
              <img
                src={LOGOS.darkText}
                alt="Anexi Consulting"
                className="h-10 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-0.5" data-testid="nav-mega-menu-trigger">
              {NAV_ITEMS.map((item) => (
                <div
                  key={item.label}
                  className="relative group"
                  onMouseEnter={() => setActiveMenu(item.label)}
                  onMouseLeave={() => setActiveMenu(null)}
                >
                  {item.external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-1 px-2.5 py-2 text-[11px] font-medium tracking-wide transition-all rounded-lg text-[#3D1C11]/70 hover:text-[#3D1C11] hover:bg-[#FAF5EF]`}
                    >
                      <span className="whitespace-nowrap">{item.label}</span>
                      <ArrowUpRight size={10} className="opacity-40" />
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      className={`flex items-center gap-1 px-2.5 py-2 text-[11px] font-medium tracking-wide transition-all rounded-lg ${
                        location.pathname === item.href
                          ? 'text-[#E8642C] bg-[#E8642C]/5'
                          : 'text-[#3D1C11]/70 hover:text-[#3D1C11] hover:bg-[#FAF5EF]'
                      }`}
                    >
                      <span className="whitespace-nowrap">{item.label}</span>
                      {item.subItems && item.subItems.length > 0 && (
                        <ChevronDown size={10} className={`opacity-40 transition-transform duration-200 ${activeMenu === item.label ? 'rotate-180' : ''}`} />
                      )}
                    </Link>
                  )}

                  {/* Dropdown */}
                  <AnimatePresence>
                    {activeMenu === item.label && item.subItems && item.subItems.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                        className="absolute top-full left-0 mt-2 w-[280px] bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] border border-[#3D1C11]/5 p-2 z-50"
                      >
                        {item.subItems.map((sub, idx) => (
                          <Link
                            key={sub.label}
                            to={sub.href}
                            className="block px-4 py-3 text-sm text-[#1A1008] hover:text-[#E8642C] hover:bg-[#FAF5EF] rounded-xl transition-all group/item"
                          >
                            <span className="group-hover/item:translate-x-0.5 inline-block transition-transform">
                              {sub.label}
                            </span>
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-4">
              <Link
                to="/contact"
                className="group bg-[#1A1008] text-white px-6 py-2.5 rounded-full text-xs font-semibold hover:bg-[#3D1C11] transition-colors flex items-center gap-2 whitespace-nowrap"
                data-testid="header-talk-to-us-button"
              >
                Contact Us
                <ArrowUpRight size={13} className="opacity-60 group-hover:opacity-100 transition-opacity" />
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2.5 rounded-full hover:bg-[#F5D0A9]/30 z-50 transition-colors"
              data-testid="mobile-nav-open-button"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={22} className="text-[#3D1C11]" /> : <Menu size={22} className="text-[#3D1C11]" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-[rgba(61,28,17,0.06)] overflow-hidden"
            >
              <div className="max-h-[70vh] overflow-y-auto px-6 py-6 space-y-1">
                {NAV_ITEMS.map((item) => (
                  <Link
                    key={item.label}
                    to={item.href}
                    className={`block px-4 py-3.5 text-[15px] font-medium rounded-xl transition-colors ${
                      location.pathname === item.href
                        ? 'text-[#E8642C] bg-[#F5D0A9]/15'
                        : 'text-[#2D2019] hover:bg-[#FAF5EF]'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
                <div className="pt-4 mt-3 border-t border-[rgba(61,28,17,0.06)]">
                  <Link
                    to="/contact"
                    className="block w-full text-center bg-[#1A1008] text-white px-5 py-3.5 rounded-full text-[15px] font-medium tracking-wide"
                    data-testid="mobile-nav-primary-cta-button"
                  >
                    Talk to Us
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;
